import {
  cleanDistributionValue,
  createEmptyDistributionValidation,
  DISTRIBUTION_MANDATORY_MESSAGES,
  DISTRIBUTION_OPTIONAL_MESSAGES,
  revalidateDistributionRow,
} from '../../shared/utilities/distribution-validation.util';
import {
  DistributionListGroup,
  DistributionListGroupRow,
  DistributionListUser,
  DistributionListUserEditPatch,
  GraphUserSuggestion,
  receivesNotifications,
  toUserPatch,
} from './distribution-list.model';
import { PRIMARY_SHEET_NAME } from './distribution-list-workbook';

type RowPatchKey = 'name' | 'company' | 'title' | 'email' | 'phone';
const ROW_PATCH_KEYS: RowPatchKey[] = [
  'name',
  'company',
  'title',
  'email',
  'phone',
];

/** True for the Primary (Master) tab, where phone, order and names are mandatory. */
export function isPrimaryDistributionGroup(
  group: DistributionListGroup,
  groups: DistributionListGroup[]
): boolean {
  return (
    group.id?.toLowerCase() === 'primary' ||
    group.sheetName?.trim().toUpperCase() === PRIMARY_SHEET_NAME ||
    group.name?.toLowerCase() === 'primary' ||
    group.name?.toLowerCase() === 'master' ||
    (group.isDefault && groups[0]?.id === group.id)
  );
}

/** The user with the given email (case insensitive), if any. */
export function findUserByEmail(
  users: DistributionListUser[],
  email: string
): DistributionListUser | undefined {
  const target = email.trim().toLowerCase();
  return target
    ? users.find(user => user.email.trim().toLowerCase() === target)
    : undefined;
}

/**
 * The user that already has `email` in one of the `groupIds` sheets, comparing the row of that
 * sheet (not the user's top level email). Every representation of the `editingIds` users is the
 * current user and never conflicts; Name, Phone and HAL ID are not identity here.
 */
export function findEmailConflict(
  users: DistributionListUser[],
  editingIds: string[],
  groupIds: string[],
  email: string
): DistributionListUser | undefined {
  const target = cleanDistributionValue(email).toLowerCase();
  if (!target) {
    return undefined;
  }
  return users.find(
    user =>
      !editingIds.includes(user.id) &&
      groupIds.some(
        groupId =>
          user.groupIds.includes(groupId) &&
          cleanDistributionValue((user.rowsByGroup?.[groupId] ?? user).email).toLowerCase() === target
      )
  );
}

/** Names compare as the same person ignoring case, accents, punctuation, spacing and word order. */
export function sameDisplayName(a: string, b: string): boolean {
  const key = (value: string): string =>
    cleanDistributionValue(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/gi, 'd')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .sort()
      .join(' ');
  return !!key(a) && key(a) === key(b);
}

/**
 * True when the email is a system user whose name is not `name`: it belongs to another user. A
 * missing user, or a system user or edited user without a name, cannot be compared and is not one.
 */
export function isOtherSystemUser(
  systemUser: Pick<GraphUserSuggestion, 'displayName'> | undefined,
  name: string
): boolean {
  return (
    !!systemUser &&
    !!cleanDistributionValue(systemUser.displayName) &&
    !!cleanDistributionValue(name) &&
    !sameDisplayName(systemUser.displayName, name)
  );
}

/** Groups of the user whose row shows `email`. */
export function emailGroupIds(user: DistributionListUser, email: string): string[] {
  const target = cleanDistributionValue(email).toLowerCase();
  return target
    ? user.groupIds.filter(
        id => cleanDistributionValue((user.rowsByGroup?.[id] ?? user).email).toLowerCase() === target
      )
    : [];
}

/**
 * The records that are the same person as `editing` when its email is N/A: email is the canonical
 * key, so a record holding `email` only on sheets `editing` is not on is the same user. A record
 * holding it on a sheet of `editing` is a duplicate, not a match. Empty when `editing` has an email.
 */
export function findLinkedUsers(
  users: DistributionListUser[],
  editing: DistributionListUser,
  email: string
): DistributionListUser[] {
  if (cleanDistributionValue(editing.email)) {
    return [];
  }
  return users.filter(user => {
    const held = user.id === editing.id ? [] : emailGroupIds(user, email);
    return held.length > 0 && !held.some(id => editing.groupIds.includes(id));
  });
}

/**
 * Joins the linked records into the edited user (N/A email) for the selected sheets: the user takes
 * their rows of `patch.groupIds`, and a record left without a sheet is dropped.
 */
export function mergeLinkedUsers(
  users: DistributionListUser[],
  targetId: string,
  patch: DistributionListUserEditPatch
): DistributionListUser[] {
  const target = users.find(user => user.id === targetId);
  if (!target || patch.email === undefined || !patch.groupIds) {
    return users;
  }
  const selected = patch.groupIds;
  const moved = new Map(
    findLinkedUsers(users, target, patch.email)
      .map(user => [user.id, emailGroupIds(user, patch.email!).filter(id => selected.includes(id))] as const)
      .filter(([, groupIds]) => groupIds.length > 0)
  );
  if (!moved.size) {
    return users;
  }

  const groupIds = [...target.groupIds];
  const mutedGroupIds = [...target.mutedGroupIds];
  const rowsByGroup: Record<string, DistributionListGroupRow> = { ...target.rowsByGroup };
  users.forEach(user => {
    moved.get(user.id)?.forEach(id => {
      if (!groupIds.includes(id)) {
        groupIds.push(id);
      }
      if (user.rowsByGroup?.[id]) {
        rowsByGroup[id] = user.rowsByGroup[id];
      }
      if (user.mutedGroupIds.includes(id) && !mutedGroupIds.includes(id)) {
        mutedGroupIds.push(id);
      }
    });
  });
  const joined: DistributionListUser = {
    ...target,
    groupIds,
    mutedGroupIds,
    ...(Object.keys(rowsByGroup).length ? { rowsByGroup } : {}),
  };

  return users
    .map(user => {
      const leaving = moved.get(user.id);
      if (user.id === targetId) {
        return joined;
      }
      return leaving ? pruneUserGroups(user, user.groupIds.filter(id => !leaving.includes(id))) : user;
    })
    .filter(user => user.groupIds.length > 0 || !moved.has(user.id));
}

/** Applies a user edit to the list: first joins the linked records (N/A email), then the patch itself. */
export function applyUsersEdit(
  users: DistributionListUser[],
  groups: DistributionListGroup[],
  userIds: string[],
  patch: DistributionListUserEditPatch
): DistributionListUser[] {
  const merged = userIds.length === 1 ? mergeLinkedUsers(users, userIds[0], patch) : users;
  return merged.map(user =>
    userIds.includes(user.id) ? applyUserPatch(user, patch, groups, merged) : user
  );
}

function isPrimaryGroupId(
  groupId: string,
  groups: DistributionListGroup[]
): boolean {
  const group = groups.find(item => item.id === groupId);
  return group
    ? isPrimaryDistributionGroup(group, groups)
    : groupId.toLowerCase() === 'primary';
}

/** Position for a row appended at the end of a tab. */
export function getNextGroupPosition(
  groupId: string,
  users: DistributionListUser[]
): number {
  let count = 0;
  let maxPosition = -1;
  for (const user of users) {
    if (!user.groupIds.includes(groupId)) {
      continue;
    }
    count++;
    maxPosition = Math.max(
      maxPosition,
      user.rowsByGroup?.[groupId]?.position ?? -1
    );
  }
  return Math.max(count, maxPosition + 1);
}

/** A known HAL ID silences the HAL ID warning whatever the typed company; unknown falls back to company. */
function hasHalId(user: DistributionListUser): true | undefined {
  return user.halId ? true : undefined;
}

/** Builds the per tab row of a user that was added or moved to a tab on the page. */
export function buildGroupRow(
  user: DistributionListUser,
  position: number,
  isPrimary: boolean
): DistributionListGroupRow {
  const name = cleanDistributionValue(user.name);
  return revalidateDistributionRow<DistributionListGroupRow>(
    {
      name: name || 'N/A',
      company: user.company,
      title: user.title,
      email: user.email,
      phone: user.phone,
      namesBothBlank: !name,
      nameHover: name,
      validation: createEmptyDistributionValidation(),
      position,
      isNew: true,
    },
    isPrimary,
    hasHalId(user)
  );
}

/** Adds the per tab rows (with validation) to a user created on the page. */
export function withGroupRows(
  user: DistributionListUser,
  groups: DistributionListGroup[],
  allUsers: DistributionListUser[]
): DistributionListUser {
  const rowsByGroup: Record<string, DistributionListGroupRow> = {};
  user.groupIds.forEach(groupId => {
    rowsByGroup[groupId] = buildGroupRow(
      user,
      getNextGroupPosition(groupId, allUsers),
      isPrimaryGroupId(groupId, groups)
    );
  });
  return { ...user, rowsByGroup };
}

/**
 * Stores a resolved Name on a tab row and recomputes its first / last name checks, the only name
 * checks that depend on the value (same split as the workbook writer).
 */
function withResolvedName(
  row: DistributionListGroupRow,
  name: string,
  isPrimary: boolean
): DistributionListGroupRow {
  const [first = '', ...rest] = name.trim().split(/\s+/).filter(Boolean);
  const last = rest.join(' ');
  const mandatory = {
    ...createEmptyDistributionValidation().mandatory,
    ...row.validation?.mandatory,
    firstName: isPrimary && !first,
    lastName: isPrimary && !last,
  };
  const optional = {
    ...createEmptyDistributionValidation().optional,
    ...row.validation?.optional,
    firstName: !first,
    lastName: !last,
  };
  let nameHover = name;
  if (isPrimary) {
    if (mandatory.firstName) {
      nameHover = DISTRIBUTION_MANDATORY_MESSAGES.firstName;
    } else if (mandatory.lastName) {
      nameHover = DISTRIBUTION_MANDATORY_MESSAGES.lastName;
    }
  } else if (optional.firstName) {
    nameHover = DISTRIBUTION_OPTIONAL_MESSAGES.firstName;
  } else if (optional.lastName) {
    nameHover = DISTRIBUTION_OPTIONAL_MESSAGES.lastName;
  }
  return {
    ...row,
    name,
    namesBothBlank: false,
    nameHover,
    validation: { mandatory, optional },
  };
}

/**
 * Applies an edit patch to a user and keeps its per tab rows in sync: edited
 * values are written to every row, the validation is recomputed with the same
 * rules as the upload preview, and rows of added/removed tabs are created/dropped.
 * A resolved `name` is written to every row like the other shared fields.
 * `originalEmail` is never stored; leaving a tab also drops its notification setting.
 */
export function applyUserPatch(
  user: DistributionListUser,
  editPatch: DistributionListUserEditPatch,
  groups: DistributionListGroup[],
  allUsers: DistributionListUser[]
): DistributionListUser {
  const patch = toUserPatch(editPatch);
  const updated: DistributionListUser = {
    ...user,
    ...patch,
    mutedGroupIds: user.mutedGroupIds.filter(id =>
      (patch.groupIds ?? user.groupIds).includes(id)
    ),
  };
  if (!user.rowsByGroup) {
    return updated;
  }

  const rowPatch: Partial<Pick<DistributionListUser, RowPatchKey>> = {};
  ROW_PATCH_KEYS.forEach(key => {
    const value = patch[key];
    if (value !== undefined) {
      rowPatch[key] = value;
    }
  });
  const hasRowChange = Object.keys(rowPatch).length > 0;

  const rowsByGroup: Record<string, DistributionListGroupRow> = {};
  updated.groupIds.forEach(groupId => {
    const isPrimary = isPrimaryGroupId(groupId, groups);
    const existing = user.rowsByGroup?.[groupId];
    if (!existing) {
      rowsByGroup[groupId] = buildGroupRow(
        updated,
        getNextGroupPosition(groupId, allUsers),
        isPrimary
      );
    } else if (hasRowChange) {
      const revalidated = revalidateDistributionRow(
        { ...existing, ...rowPatch },
        isPrimary,
        hasHalId(updated)
      );
      rowsByGroup[groupId] = rowPatch.name
        ? withResolvedName(revalidated, rowPatch.name, isPrimary)
        : revalidated;
    } else {
      rowsByGroup[groupId] = existing;
    }
  });

  return { ...updated, rowsByGroup };
}

/** Removes the membership (and row) of groups that no longer exist. */
export function pruneUserGroups(
  user: DistributionListUser,
  groupIds: string[]
): DistributionListUser {
  const kept = user.groupIds.filter(id => groupIds.includes(id));
  const mutedGroupIds = user.mutedGroupIds.filter(id => kept.includes(id));
  if (!user.rowsByGroup) {
    return { ...user, groupIds: kept, mutedGroupIds };
  }
  const rowsByGroup: Record<string, DistributionListGroupRow> = {};
  kept.forEach(id => {
    if (user.rowsByGroup?.[id]) {
      rowsByGroup[id] = user.rowsByGroup[id];
    }
  });
  return { ...user, groupIds: kept, mutedGroupIds, rowsByGroup };
}

/**
 * Removes the given users from one list only. Their membership of the other
 * lists is kept; users that no longer belong to any list are dropped.
 */
export function removeUsersFromGroup(
  users: DistributionListUser[],
  userIds: string[],
  groupId: string
): DistributionListUser[] {
  return users
    .map(user =>
      userIds.includes(user.id)
        ? pruneUserGroups(
            user,
            user.groupIds.filter(id => id !== groupId)
          )
        : user
    )
    .filter(user => user.groupIds.length > 0);
}

/** The user as it appears on one tab: top level data overridden by that tab's row and notification flag. */
export function projectUserForGroup(
  user: DistributionListUser,
  groupId: string
): DistributionListUser {
  const row = user.rowsByGroup?.[groupId];
  return {
    ...user,
    ...row,
    sendNotification: receivesNotifications(user, groupId),
  };
}

/** Users of a tab, projected for that tab and in the original row order. */
export function getGroupUsers(
  users: DistributionListUser[],
  groupId: string
): DistributionListUser[] {
  const members = users.filter(user => user.groupIds.includes(groupId));
  const hasAllRows = members.every(user => !!user.rowsByGroup?.[groupId]);
  const ordered = hasAllRows
    ? [...members].sort(
        (a, b) =>
          (a.rowsByGroup?.[groupId]?.position ?? 0) -
          (b.rowsByGroup?.[groupId]?.position ?? 0)
      )
    : members;
  return ordered.map(user => projectUserForGroup(user, groupId));
}
